import dayjs from 'dayjs';
import PropTypes from 'prop-types';

import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import Flatpickr from 'react-flatpickr';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Icon from 'components/global/Icon';
import Typography from 'components/global/Typography';

import useLocale from 'hooks/useLocale';

import { faCalendarAlt, faExclamationCircle } from '@fortawesome/pro-solid-svg-icons';

import ViewField from './ViewField';
import * as S from './styled/DateInput.styled';
import { InputMessageContainer } from './styled/InputCommon.styled';

/**
 * @author Burak Unsal
 */
const DateInput = ({
	name,
	disabled = false,
	viewMode,
	placeholder,
	value,
	setValue,
	rangeMode = false,
	inline = false,
	errorMessage: errorMessageProp,
	maxDate,
	minDate,
	withoutTime,
	enableTime,
	...moreProps
}) => {
	const [referenceElement, setReferenceElement] = useState(null);
	const { getLocaleDate, getLocaleDateWithTime } = useLocale();
	const timeFn = enableTime ? getLocaleDateWithTime : getLocaleDate;
	const { t } = useTranslation();
	const formMethods = useFormContext();
	const errorMessage = formMethods?.getFieldState?.(name, formMethods?.formState)?.error?.message ?? errorMessageProp;

	useEffect(() => {
		const input = referenceElement?.flatpickr?._input;
		const calendarContainer = referenceElement?.flatpickr?.calendarContainer;

		if (input) {
			if (placeholder) {
				input.setAttribute('placeholder', t(placeholder));
			}
		}
		if (calendarContainer) {
			calendarContainer.addEventListener('mousedown', stopPopper);
			calendarContainer.addEventListener('touchend', stopPopper);
		}
		return () => {
			if (calendarContainer) {
				calendarContainer.removeEventListener('mousedown', stopPopper);
				calendarContainer.removeEventListener('touchend', stopPopper);
			}
		};
	}, [referenceElement, placeholder]);

	const stopPopper = useCallback((event) => {
		event.stopPropagation();
	}, []);

	const handleInputClick = (event) => {
		event.stopPropagation();

		referenceElement.flatpickr.altInput.focus();
		referenceElement.flatpickr.open();
	};

	return (
		<>
			<S.InputContainer {...moreProps} viewmode={viewMode || disabled}>
				{viewMode || disabled ? (
					<ViewField
						value={
							rangeMode
								? `${value?.start ? timeFn(value?.start) : ''} - ${
										value?.end ? timeFn(value?.end) : ''
									}`
								: value?.start
									? `${timeFn(value?.start)}`
									: `${value ? timeFn(value) : '-'}`
						}
						disabled={disabled}
					/>
				) : (
					<S.InputWrapper className="flatpickr" inline={inline} onClick={handleInputClick}>
						<S.IconContainer>
							<Icon icon={faCalendarAlt} color="var(--content-4)" />
						</S.IconContainer>
						<FlatpickrWrapper
							ref={setReferenceElement}
							value={value}
							setValue={setValue}
							inline={inline}
							rangeMode={rangeMode}
							error={!!errorMessage}
							maxDate={maxDate}
							minDate={minDate}
							withoutTime={withoutTime}
							enableTime={enableTime}
						/>
					</S.InputWrapper>
				)}
			</S.InputContainer>
			{errorMessage && (
				<InputMessageContainer className="error-message-container date-input-error-message-container">
					<Icon icon={faExclamationCircle} color="var(--red)" size="x-small" />
					<Typography variant="caption" color="var(--red)" noWrap>
						{errorMessage}
					</Typography>
				</InputMessageContainer>
			)}
		</>
	);
};

export const FlatpickrWrapper = forwardRef((props, ref) => {
	const {
		value,
		setValue,
		rangeMode = false,
		inline = false,
		error,
		className,
		filterMode,
		maxDate,
		minDate,
		withoutTime = false,
		enableTime = false
	} = props;

	const { DateFormat, LanguageCode } = useLocale();
	const calculatedOffset = useMemo(() => dayjs().tz().utcOffset() - dayjs().utcOffset(), []);

	const convertedValue = useMemo(
		() =>
			rangeMode
				? [
						value?.start
							? dayjs(dayjs(value?.start).valueOf() + calculatedOffset * 60000).toISOString()
							: undefined,
						value?.end
							? dayjs(dayjs(value?.end).startOf('day').valueOf() + calculatedOffset * 60000).toISOString()
							: undefined
					]
				: value?.start
					? [dayjs(dayjs(value?.start).valueOf() + calculatedOffset * 60000).toISOString()]
					: value &&
						(withoutTime
							? value
							: [dayjs(dayjs(value).valueOf() + calculatedOffset * 60000).toISOString()]),
		[value]
	);

	const handleOnChange = useCallback(
		(val) => {
			if (val.length === 0) {
				setValue('');
			} else {
				const startDate = val[0];
				const endDate = val[1];

				if (withoutTime) {
					setValue(dayjs(startDate).format('YYYY-MM-DD'));
				} else if (rangeMode || filterMode) {
					setValue({
						start: startDate
							? dayjs(dayjs(startDate).valueOf() - calculatedOffset * 60000).toISOString()
							: undefined,
						end: endDate
							? dayjs(dayjs(endDate).endOf('day').valueOf() - calculatedOffset * 60000).toISOString()
							: undefined
					});
				} else {
					setValue(
						startDate
							? dayjs(dayjs(startDate).valueOf() - calculatedOffset * 60000).toISOString()
							: undefined
					);
				}
			}
		},
		[setValue]
	);

	return (
		<S.FlatpickrContainer className={`${error ? 'error' : ''} ${className ?? ''}`} inline={inline}>
			<Flatpickr
				key={LanguageCode}
				ref={ref}
				value={convertedValue}
				onClose={handleOnChange}
				options={{
					inline,
					mode: rangeMode ? 'range' : 'single',
					dateFormat: withoutTime ? 'Y-m-d' : 'Z',
					altInput: true,
					altFormat: enableTime ? `${DateFormat.flatpickrFormat} H:i` : DateFormat.flatpickrFormat,
					altInputClass: 'alt-input',
					disableMobile: 'true',
					allowInput: 'true',
					enableTime: enableTime ? 'true' : 'false',
					// eslint-disable-next-line camelcase
					time_24hr: enableTime ? 'true' : 'false',
					...(maxDate && { maxDate }),
					...(minDate && { minDate })
				}}
			/>
		</S.FlatpickrContainer>
	);
});

FlatpickrWrapper.displayName = 'FlatpickrWrapper';

DateInput.propTypes = {
	/** Disables onclick event and UI true/false */
	disabled: PropTypes.bool,
	/** To be able to change the requirement of the input */
	required: PropTypes.bool,
	/** Label text, if it's empty only input will be shown */
	label: PropTypes.string,
	/** Description of the date input field.*/
	description: PropTypes.string,
	/** Enables view mode and instead of input we will show typography*/
	viewMode: PropTypes.bool,
	/** What should appear in the input when no value is selected ? */
	placeholder: PropTypes.string,
	/** The text that will appear in the error state */
	errorMessage: PropTypes.string,
	/** The value of the input */
	value: PropTypes.any,
	/** The function that will be called when the value of the input changes */
	setValue: PropTypes.func,
	/** Inputs loading state */
	isLoading: PropTypes.bool,
	/** Range mode */
	rangeMode: PropTypes.bool,
	/** Inline mode */
	inline: PropTypes.bool,
	/** Max date */
	maxDate: PropTypes.string,
	/** Min date */
	minDate: PropTypes.string
};

export default DateInput;
